import { cn } from "./cn";
import { marka } from "./marka";

/**
 * CANLI ÇALIŞMA İZLEME — BİLGİLENDİRME METNİ (F-LIV-07, F-LIV-08)
 *
 * ================================================================
 * NEDEN METİN KODDA SABİT, NEDEN PAYLAŞILAN BİR BİLEŞEN
 * ================================================================
 * Aynı metin üç yerden açılıyor: öğrenci uygulaması, veli portalı ve panel.
 * Üç ayrı kopya biri güncellenip ötekiler eskidiğinde, aynı kurum iki ekranda
 * iki farklı söz vermiş olurdu. Metnin iddiaları (kim görür, ne görünür, ne
 * kaydedilir) sunucu davranışıyla BİREBİR tutmak zorunda (F-LIV-08); davranış
 * değişirse önce bu metin ve `CANLI_BILGILENDIRME_SURUMU` değişir.
 *
 * Bu metin genel aydınlatma metninin yeni sürümü DEĞİL ve rıza istemiyor: yeni
 * sürüm herkesin rızasını eskitir ve onay kilidi herkesi yeniden onaya zorlardı.
 * O yüzden ekranda ONAY / KABUL düğmesi YOK; sayfa salt okunur.
 *
 * Kaynak metin: `koclukpaneli-araclar/kvkk/canli-izleme-bilgilendirme.md`
 * ("Canlı çalışma izleme hakkında bilgilendirme" başlığından itibaren).
 * Firma adı `marka`dan geliyor; metne elle yazılmıyor.
 *
 * `surum` ISTEGE BAGLI ve disaridan geliyor: `ui` paketi `contracts`a bagli
 * degil (iki paket birbirinden bagimsiz kalsin diye); surum sabiti sozlesmede.
 */
export function CanliIzlemeBilgilendirme({
  surum,
  baslikSeviyesi = 2,
  className,
}: {
  /** `CANLI_BILGILENDIRME_SURUMU` — verilirse "Sürüm 1" diye küçük yazılır. */
  surum?: string;
  /** Ana başlığın seviyesi; bölüm başlıkları bir alt seviyede. */
  baslikSeviyesi?: 1 | 2;
  className?: string;
}) {
  // Aritmetik (`+ 1`) şablon dizgisinde `h${number}` tipi üretir ve geçerli bir JSX etiketi olmaz.
  const Ana = baslikSeviyesi === 1 ? "h1" : "h2";
  const Bolum = baslikSeviyesi === 1 ? "h2" : "h3";
  const bolumSinifi = "text-notr-900 mt-1 text-sm font-semibold";
  const metinSinifi = "text-notr-700 text-sm leading-[1.6]";
  const listeSinifi = "text-notr-700 list-disc space-y-1 pl-5 text-sm leading-[1.6]";

  return (
    <article data-ui="canli-izleme-bilgilendirme" className={cn("flex flex-col gap-3", className)}>
      <Ana className="text-notr-900 text-lg font-semibold">
        Canlı çalışma izleme hakkında bilgilendirme
      </Ana>
      {surum !== undefined && <p className="text-notr-600 text-xs">Sürüm {surum}</p>}

      <p className={metinSinifi}>
        <strong>Veri sorumlusu:</strong> {marka.ad}
      </p>

      <Bolum className={bolumSinifi}>Ne yapıyoruz?</Bolum>
      <p className={metinSinifi}>
        Çalışma sayacını başlattığında, koçun ve seni takip eden eğitim ekibi şu anda çalıştığını
        anlık olarak görebilir. Örneğin &quot;Matematik · Türev testi · 32 dakikadır çalışıyor&quot;
        bilgisi görünür.
      </p>

      <Bolum className={bolumSinifi}>Hangi bilgiler görünür?</Bolum>
      <ul className={listeSinifi}>
        <li>adın ve soyadının baş harfi;</li>
        <li>çalıştığın ders ve konu ya da görev;</li>
        <li>sayacı ne zaman başlattığın ve ne kadar süredir çalıştığın.</li>
      </ul>
      <p className={metinSinifi}>
        Bu özellik için <strong>yeni bir bilgi toplanmaz</strong>. Gösterilen bilgiler, çalışma
        sayacının zaten kaydettiği bilgilerdir. Canlı görünümün kendisi ayrıca kaydedilmez. Listeye
        kimin ne zaman baktığı yalnız güvenlik amacıyla sistem kayıtlarında tutulur (aynı kişi için
        en fazla 5 dakikada bir; kayıt sistemi kısa süreli erişilemezse daha sık kayıt tutulabilir;
        kayıtta senin adın, dersin ya da süren yer almaz).
      </p>

      <Bolum className={bolumSinifi}>Kimler görebilir?</Bolum>
      <ul className={listeSinifi}>
        <li>
          <strong>Koçun:</strong> yalnız kendi öğrencilerini görür. Koçun izinliyken yerine bakan
          vekil koç da o süre boyunca görebilir.
        </li>
        <li>
          <strong>Akademik lider:</strong> yalnız kendi ekibindeki öğrencileri görür.
        </li>
        <li>
          <strong>Operasyon yöneticisi ve süper yönetici:</strong> destek ve denetim için görebilir.
          Teknik yönetici göremez.
        </li>
        <li>
          Velin, satış ekibi ve diğer öğrenciler bu bilgiyi <strong>göremez</strong>.
        </li>
      </ul>

      <Bolum className={bolumSinifi}>Neden?</Bolum>
      <p className={metinSinifi}>
        Çalışırken zorlandığın bir anda koçunun zamanında destek olabilmesi ve haftalık programının
        gerçekçi planlanabilmesi için. Bu bilgi not vermek ya da cezalandırmak için kullanılmaz.
      </p>

      <Bolum className={bolumSinifi}>Hukuki sebep</Bolum>
      <p className={metinSinifi}>
        Kişisel verilerin, KVKK m.5/2-c kapsamında aramızdaki koçluk hizmeti sözleşmesinin kurulması
        ve ifası için gerekli olması ile m.5/2-f kapsamında hizmetin kalitesine yönelik meşru
        menfaat.
      </p>

      <Bolum className={bolumSinifi}>Ne kadar süre saklanır?</Bolum>
      <p className={metinSinifi}>
        Canlı görünüm ayrıca saklanmaz. Çalışma sayacı kayıtları, genel aydınlatma metninde yazan
        çalışma kayıtları saklama süresine tabidir.
      </p>

      <Bolum className={bolumSinifi}>Hakların</Bolum>
      <p className={metinSinifi}>
        KVKK m.11 kapsamındaki haklarını genel aydınlatma metninde yazan başvuru yolu ile
        kullanabilirsin: bilgi talep etme, düzeltme, silme, itiraz ve diğer haklar.
      </p>
      <p className={metinSinifi}>
        Çalışma sayacını kullanmak zorunda değilsin. Süreni elle de girebilirsin. Elle girilen süre
        canlı görünümde yer almaz.
      </p>
    </article>
  );
}
